/**
 * Accent derived from the agency's charter colour (§2 bis « Multi-agences »): each agency keeps the
 * two themes, its brand colour replaces the accent, with automatic contrast correction (AA).
 */

import { AA_NORMAL_TEXT, type Rgba, contrastRatio, over, parseColor } from "./contrast";
import { type ColorTokens, type ThemeName, themes } from "./tokens";

export type AccentKey =
  | "accent"
  | "accentFill"
  | "accentFillHover"
  | "accentFillPressed"
  | "onAccentFill"
  | "accentTint"
  | "accentTintHover"
  | "focusRing"
  | "accentGlow"
  | "brand600"
  | "focus";

export type AccentSet = Pick<ColorTokens, AccentKey>;

interface Hsl {
  h: number;
  s: number;
  l: number;
}

function toHsl(hex: string): Hsl {
  const { r, g, b } = parseColor(hex);
  const [rn, gn, bn] = [r / 255, g / 255, b / 255];
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h =
    max === rn ? (gn - bn) / d + (gn < bn ? 6 : 0) : max === gn ? (bn - rn) / d + 2 : (rn - gn) / d + 4;
  return { h: h * 60, s, l };
}

function toHex({ h, s, l }: Hsl): string {
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const hex = (v: number) =>
    Math.round(Math.max(0, Math.min(1, v)) * 255)
      .toString(16)
      .padStart(2, "0")
      .toUpperCase();
  return `#${hex(f(0))}${hex(f(8))}${hex(f(4))}`;
}

const rgba = (hex: string, alpha: number): string => {
  const { r, g, b } = parseColor(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/** Move lightness step by step until `ok` holds (or the end of the range is reached). */
function adjust(color: Hsl, step: number, ok: (hex: string) => boolean): Hsl {
  let current = { ...color };
  for (let i = 0; i < 100 && !ok(toHex(current)); i += 1) {
    current = { ...current, l: Math.max(0, Math.min(1, current.l + step)) };
  }
  return current;
}

const shift = (color: Hsl, dl: number): string =>
  toHex({ ...color, l: Math.max(0, Math.min(1, color.l + dl)) });

/** Accent tokens for one theme, corrected so that every text/fill pair reaches WCAG AA. */
export function deriveAccent(brandHex: string, theme: ThemeName): AccentSet {
  const base = themes[theme].color;
  const brand = toHsl(brandHex);
  const onFill = "#FFFFFF";
  const fillOk = (hex: string) => contrastRatio(onFill, hex) >= AA_NORMAL_TEXT + 0.2;
  // Wallpaper glow: the agency hue at a fixed mid lightness, so that a black or a pale charter never
  // darkens or washes out the canvas under the text (AA tested for every charter).
  const glow = toHex({ h: brand.h, s: Math.min(brand.s, 0.95), l: theme === "light" ? 0.56 : 0.45 });
  const accentGlow = rgba(glow, theme === "light" ? 0.12 : 0.2);
  // Accent text must stay readable on the layers AND on the wallpaper under that glow.
  const wallpaper: Rgba[] = themes[theme].wallpaper.base.map((stop) => over(parseColor(accentGlow), parseColor(stop)));
  const readableOn = (layers: string[]) => (hex: string) =>
    [...layers.map((bg) => parseColor(bg)), ...wallpaper].every((bg) => contrastRatio(hex, bg) >= AA_NORMAL_TEXT + 0.2);

  if (theme === "light") {
    const fill = adjust(brand, -0.02, fillOk);
    const text = adjust(fill, -0.02, readableOn([base.bg, base.bgGrouped]));
    const textHex = toHex(text);
    return {
      accent: textHex,
      brand600: textHex,
      focus: textHex,
      accentFill: toHex(fill),
      accentFillHover: shift(fill, -0.04),
      accentFillPressed: shift(fill, -0.08),
      onAccentFill: onFill,
      accentTint: rgba(toHex(fill), 0.09),
      accentTintHover: rgba(toHex(fill), 0.14),
      focusRing: rgba(toHex(fill), 0.28),
      accentGlow,
    };
  }
  // Dark: a light tone for text and icons, a deeper saturated tone for fills with white text.
  const text = adjust({ ...brand, l: Math.max(brand.l, 0.62) }, 0.02, readableOn([base.bg, base.bgElevated, base.bgTertiary]));
  const fill = adjust({ ...brand, l: Math.max(Math.min(brand.l, 0.52), 0.4) }, -0.02, fillOk);
  const textHex = toHex(text);
  return {
    accent: textHex,
    brand600: textHex,
    focus: textHex,
    accentFill: toHex(fill),
    accentFillHover: shift(fill, 0.03),
    accentFillPressed: shift(fill, -0.04),
    onAccentFill: onFill,
    accentTint: rgba(textHex, 0.14),
    accentTintHover: rgba(textHex, 0.2),
    focusRing: rgba(textHex, 0.4),
    accentGlow,
  };
}

const kebab = (key: string): string => key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);

/** CSS overriding the accent variables for both themes (injected at runtime by the apps). */
export function accentCss(brandHex: string): string {
  const vars = (set: AccentSet) =>
    Object.entries(set)
      .map(([k, v]) => `--ao-${kebab(k)}: ${v};`)
      .join(" ");
  const dark = vars(deriveAccent(brandHex, "dark"));
  return [
    `:root { ${vars(deriveAccent(brandHex, "light"))} }`,
    `[data-theme="dark"] { ${dark} }`,
    `@media (prefers-color-scheme: dark) { :root:not([data-theme="light"]) { ${dark} } }`,
  ].join("\n");
}
